import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";
import { CATEGORY_LABELS, SUBCATEGORY_LABELS, SUBCATEGORIES } from "../lib/constants";
import { Link } from "react-router-dom";

export default function CategoryPage() {
    const { category, subcategory } = useParams();
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const label = subcategory
        ? SUBCATEGORY_LABELS[subcategory]
        : CATEGORY_LABELS[category] || category;

    const fetchPage = useCallback(async (p) => {
        const params = { category, page: p, page_size: 12 };
        if (subcategory) params.subcategory = subcategory;
        const { data } = await api.get("/articles", { params });
        return data;
    }, [category, subcategory]);

    useEffect(() => {
        setLoading(true);
        setPage(1);
        fetchPage(1).then((data) => {
            setItems(data.items);
            setHasMore(data.has_more);
        }).catch(() => setItems([])).finally(() => setLoading(false));
    }, [category, subcategory, fetchPage]);

    const loadMore = async () => {
        setLoadingMore(true);
        const next = page + 1;
        const data = await fetchPage(next);
        setItems((prev) => [...prev, ...data.items]);
        setPage(next);
        setHasMore(data.has_more);
        setLoadingMore(false);
    };

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="category-page">
            <div className="border-b-2 border-ink pb-4 mb-8">
                <span className="font-mono text-xs uppercase tracking-widest text-accent">
                    {subcategory ? "Personal Finance" : "Section"}
                </span>
                <h1 className="font-serif text-4xl sm:text-5xl font-black mt-1" data-testid="category-title">{label}</h1>
            </div>

            {category === "personal-finance" && (
                <div className="flex flex-wrap gap-2 mb-10">
                    <Link to="/category/personal-finance" className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wider border transition-colors duration-150 ${!subcategory ? "bg-ink text-white border-ink" : "border-ink hover:bg-muted"}`}>All</Link>
                    {SUBCATEGORIES.map((s) => (
                        <Link key={s} to={`/category/personal-finance/${s}`} data-testid={`subcat-chip-${s}`} className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wider border transition-colors duration-150 ${subcategory === s ? "bg-ink text-white border-ink" : "border-ink hover:bg-muted"}`}>
                            {SUBCATEGORY_LABELS[s]}
                        </Link>
                    ))}
                </div>
            )}

            {loading ? (
                <p className="font-mono text-sm text-muted-foreground animate-pulse py-16 text-center">Loading stories…</p>
            ) : items.length === 0 ? (
                <p className="font-mono text-sm text-muted-foreground py-16 text-center" data-testid="category-empty">No stories in this section yet.</p>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                        {items.map((a) => (
                            <ArticleCard key={a.id} article={a} />
                        ))}
                    </div>
                    {hasMore && (
                        <div className="mt-14 text-center">
                            <button
                                onClick={loadMore}
                                disabled={loadingMore}
                                data-testid="load-more-btn"
                                className="px-10 py-3 border-2 border-ink font-mono text-xs uppercase tracking-widest hover:bg-ink hover:text-white transition-colors duration-150 disabled:opacity-50"
                            >
                                {loadingMore ? "Loading…" : "Load More"}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
